import { useEffect, useState } from "react";
import api, { apiErr, fmtMoney, downloadFile } from "@/lib/api";
import { StatusPill } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDown, Loader2, Sigma } from "lucide-react";
import { toast } from "sonner";

export function YearSummary() {
  const [year, setYear] = useState(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/billing/year", { params: year ? { year } : {} }).then((r) => { setData(r.data); if (!year) setYear(r.data.year); }).catch((e) => toast.error(apiErr(e)));
  }, [year]);

  const download = async () => {
    setBusy(true);
    try { await downloadFile("/billing/expense-report", `vuzurent-expense-report-${data.year}.pdf`, { year: data.year }); toast.success("Expense report downloaded"); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (!data) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="billing-year-summary">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2"><Sigma className="h-5 w-5 text-primary" /><h3 className="font-display font-bold text-lg text-slate-900">Running total · {data.year}</h3></div>
        <div className="ml-auto flex items-center gap-2">
          <Select value={String(data.year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="h-9 w-28" data-testid="billing-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{data.years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={download} disabled={busy} data-testid="billing-expense-report">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} Expense report PDF</Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-slate-500 text-xs uppercase tracking-wider border-b border-slate-200">
            <tr><th className="text-left font-semibold py-2.5">Month</th><th className="text-right font-semibold py-2.5">Fee paid</th><th className="text-right font-semibold py-2.5">Running total</th><th className="text-right font-semibold py-2.5">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.months.map((m) => (
              <tr key={m.period} data-testid={`billing-year-row-${m.period}`} className={m.status === "n/a" ? "text-slate-300" : ""}>
                <td className="py-2.5 font-medium text-slate-800">{m.label}</td>
                <td className="py-2.5 text-right font-mono text-xs font-semibold">{m.paid ? fmtMoney(m.amount) : <span className="text-slate-300">—</span>}</td>
                <td className="py-2.5 text-right font-mono text-xs font-bold text-slate-900" data-testid={`billing-running-${m.period}`}>{fmtMoney(m.running_total)}</td>
                <td className="py-2.5 text-right">{m.status === "n/a" ? <span className="text-[11px]">before signup</span> : <StatusPill status={m.status} />}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-200">
              <td className="py-3 font-display font-bold text-slate-900">Annual grand total</td>
              <td className="py-3 text-right text-xs text-slate-500">{data.months_paid} month(s) paid</td>
              <td className="py-3 text-right font-display font-extrabold text-lg text-slate-900" data-testid="billing-annual-total">{fmtMoney(data.total_paid)}</td>
              <td className="py-3 text-right text-xs text-rose-600">{data.total_due > 0 ? `${fmtMoney(data.total_due)} unpaid` : ""}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
